// src/app/components/form-builder/FormSettingsTabs.RegistrationTab.tsx
// Turns a form into a customer/wholesale registration form: on submit (or on
// merchant approval), the submitter is created/updated as a real Shopify
// customer — tagged, with an address, and optional custom metafields —
// using a mapping from Shopify's standard customer fields to this form's own
// field names.

import React from "react";
import { Trash2, Plus, UserPlus } from "lucide-react";
import { Form } from "./types";

// ─── Shared micro-components (matches FormSettingsTabs.tsx's current styling) ─

const Inp = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...p }, ref) => (
    <input ref={ref} {...p} className={`w-full px-3 py-2 text-sm border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none transition-colors ${className ?? ''}`} />
  )
);
const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold text-ink-500 mb-1.5 font-sans">{children}</label>
);
const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...p }) => (
  <select {...p} className={`w-full px-3 py-2 text-sm border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none ${className ?? ''}`} />
);
const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={`mb-4 ${className ?? ''}`}><Label>{label}</Label>{children}</div>
);
const Card: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div className="bg-white border border-ink-200 rounded-xl p-5 mb-4">
    <p className="text-sm font-semibold text-ink-900 font-sans mb-1">{title}</p>
    {sub && <p className="text-xs text-ink-500 font-sans mb-4">{sub}</p>}
    {children}
  </div>
);
const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; sub?: string }> = ({ checked, onChange, label, sub }) => (
  <div className="flex items-center justify-between py-2.5">
    <div><p className="text-sm font-medium text-ink-900 font-sans">{label}</p>{sub && <p className="text-xs text-ink-500 font-sans">{sub}</p>}</div>
    <button type="button" onClick={() => onChange(!checked)} className={`relative flex-shrink-0 transition-colors ${checked ? 'bg-brand-500' : 'bg-borderGreyColor'}`}
      style={{ width: 32, height: 18, borderRadius: 9 }}>
      <span className={`absolute top-[2px] w-[14px] h-[14px] bg-white rounded-full shadow transition-all ${checked ? 'left-[16px]' : 'left-[2px]'}`} />
    </button>
  </div>
);
const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border rounded-lg px-3 py-2.5 text-xs font-sans mb-4 bg-brand-100 border-brand-500/20 text-brand-600">{children}</div>
);
const G2: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="grid grid-cols-2 gap-4">{children}</div>;

// ─── Standard Shopify customer fields this form can map to ────────────────────

const CUSTOMER_FIELDS: { key: string; label: string; required?: boolean }[] = [
  { key: 'email', label: 'Email', required: true },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'address1', label: 'Address Line 1' },
  { key: 'address2', label: 'Address Line 2' },
  { key: 'city', label: 'City' },
  { key: 'province', label: 'Province / State' },
  { key: 'country', label: 'Country' },
  { key: 'zip', label: 'ZIP / Postal Code' },
  { key: 'company', label: 'Company' },
];

const METAFIELD_TYPES = [
  { value: 'single_line_text_field', label: 'Single line text' },
  { value: 'multi_line_text_field', label: 'Multi-line text' },
  { value: 'number_integer', label: 'Integer' },
  { value: 'number_decimal', label: 'Decimal' },
  { value: 'boolean', label: 'True/false' },
  { value: 'url', label: 'URL' },
];

export const RegistrationTab: React.FC<{
  form: Form;
  onChange: (f: Form) => void;
}> = ({ form, onChange }) => {
  const isOn = !!form.is_registration_form;
  const mapping = form.registration_field_mapping ?? {};
  const metafields = form.registration_metafields ?? [];
  const tags = form.registration_tags ?? [];

  const set = (k: keyof Form, v: any) => onChange({ ...form, [k]: v });
  const setMapping = (key: string, fieldName: string) =>
    onChange({ ...form, registration_field_mapping: { ...mapping, [key]: fieldName } });

  const fieldOpts = [
    { value: '', label: '— not mapped —' },
    ...(form.fields ?? []).map(f => ({
      value: f.name,
      label: typeof f.label === 'object' ? (f.label as any).en ?? f.name : String(f.label ?? f.name),
    })),
  ];

  const addMetafield = () =>
    onChange({
      ...form,
      registration_metafields: [...metafields, { namespace: 'custom', key: '', type: 'single_line_text_field', form_field: '' }],
    });
  const updMetafield = (idx: number, patch: Partial<typeof metafields[0]>) =>
    onChange({ ...form, registration_metafields: metafields.map((m, i) => i === idx ? { ...m, ...patch } : m) });
  const delMetafield = (idx: number) =>
    onChange({ ...form, registration_metafields: metafields.filter((_, i) => i !== idx) });

  return (
    <>
      <Card title="Customer Registration" sub="Create the submitter as a real Shopify customer — for wholesale/B2B signup, VIP registration, and similar forms">
        <Toggle
          checked={isOn}
          onChange={v => set('is_registration_form', v)}
          label="This is a customer registration form"
          sub="On submit (or after approval), the submitter becomes a Shopify customer"
        />
      </Card>

      {isOn && (
        <>
          <Card title="Approval">
            <Field label="When should the Shopify customer be created?">
              <Sel
                value={form.registration_approval_mode ?? 'manual'}
                onChange={e => set('registration_approval_mode', e.target.value as 'auto' | 'manual')}
              >
                <option value="manual">Manual — after I approve the submission</option>
                <option value="auto">Automatic — as soon as the form is submitted</option>
              </Sel>
            </Field>
            {(form.registration_approval_mode ?? 'manual') === 'manual' && (
              <InfoBox>Approve submissions from the Submissions tab to create the customer — the same Approval workflow already used elsewhere in this form.</InfoBox>
            )}
          </Card>

          <Card title="Tags" sub="Applied to the customer on creation (comma-separated)">
            <Inp
              defaultValue={tags.join(', ')}
              placeholder="wholesale, b2b"
              onBlur={e => set('registration_tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
            />
          </Card>

          <Card title="Field Mapping" sub="Which of your form's fields supplies each Shopify customer field">
            <G2>
              {CUSTOMER_FIELDS.map(cf => (
                <Field key={cf.key} label={cf.label + (cf.required ? ' *' : '')}>
                  <Sel value={mapping[cf.key] ?? ''} onChange={e => setMapping(cf.key, e.target.value)}>
                    {fieldOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Sel>
                </Field>
              ))}
            </G2>
            {!mapping.email && (
              <InfoBox>Map the Email field — without it, no customer can be created.</InfoBox>
            )}
          </Card>

          <Card title="Metafields" sub="Store extra business fields (e.g. Tax ID) on the customer as custom metafields">
            {metafields.map((mf, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end mb-3 pb-3 border-b border-ink-100 last:border-0 last:mb-0 last:pb-0">
                <Field label="Namespace">
                  <Inp value={mf.namespace} onChange={e => updMetafield(idx, { namespace: e.target.value })} placeholder="custom" />
                </Field>
                <Field label="Key">
                  <Inp value={mf.key} onChange={e => updMetafield(idx, { key: e.target.value })} placeholder="tax_id" />
                </Field>
                <Field label="Type">
                  <Sel value={mf.type} onChange={e => updMetafield(idx, { type: e.target.value })}>
                    {METAFIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Sel>
                </Field>
                <Field label="Form Field">
                  <Sel value={mf.form_field} onChange={e => updMetafield(idx, { form_field: e.target.value })}>
                    {fieldOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Sel>
                </Field>
                <button onClick={() => delMetafield(idx)} className="p-2 rounded-lg hover:bg-red-50 mb-4">
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              </div>
            ))}
            <button onClick={addMetafield} className="flex items-center gap-2 px-3 py-2 bg-brand-500 text-white rounded-lg text-xs font-medium font-sans hover:bg-brand-600">
              <Plus className="h-3.5 w-3.5" /> Add Metafield
            </button>
          </Card>
        </>
      )}
    </>
  );
};

export const RegistrationTabIcon = UserPlus;
